import { SelectOptionWithDarkMode } from './DropDownTypes'

export interface Admins {
  admins: Admin[]
}

export interface Admin {
  name: string
  email: string
  roles: SelectOptionWithDarkMode[]
  inviteAccepted?: boolean
  inviteSentDate?: string
}

export interface AddThreatHunterUser {
  name: string
  email: string
}

export interface AdminResponse {
  admins: {
    name: string
    email: string
    roles: string
    inviteAccepted?: boolean
    inviteSentDate?: string
  }[]
}

export interface AuditLogs {
  data: AuditLog[]
  totalPages: number
}

export interface AuditLog {
  createdAt: string
  createdBy: string
  action: string
  actionDetails: string
  metadata: string
}

export interface AuditLogFilter {
  fromDateTime: string
  toDateTime: string
}

export interface BillingInfo {
  card: Card
  subscription: Subscription
}

export interface Card {
  lastFour: string
  network: string
}

export interface Subscription {
  status: string
  invoice: boolean
  amountDue: number
  renewDate: number
  cancelDate: string
}

export interface Plan {
  status: string
  statusMessage: string
  relevantDate: string
}

export type AddSsoMemberInputs = {
  clientId: string
  clientSecret: string
  tenant: string
}

export type AddSsoSettings = {
  clientId: string
  clientSecret: string
  tenant: string
  provider: string
  slug: string
}

export interface ApiKey {
  token: string
  expiresAt: string
}

export interface WebhookSecret {
  secret: string
  webhookType: WebhookType
}

export interface WebhookRequest {
  headers: string | undefined
  url: string
  webhookType: WebhookType
}

export interface SaveWebhookRequest {
  authHeader: string
  secret: string
  url: string
  webhookType: WebhookType
}

export interface FetchedWebhookRequest {
  secret: string
  headers: string
  url: string
  webhookType: WebhookType
}

export interface WebhookPayload {
  secret: string
  headers: WebhookHeaders
  url: string
  webhookType: WebhookType
}

export interface WebhookHeaders {
  Authorization: string
}

export enum WebhookType {
  DEVICE_STATE = 'DEVICE_STATE',
  ALERT = 'ALERT',
  AUDIT_LOG = 'AUDIT_LOG',
}

export interface ConditionalAccessResponse {
  tenant: string
  clientId: string
  clientSecret: string
  userExtension: string
  groupId: string
  provider: string
  active: boolean
}

export interface ConditionalAccessStatusResponse {
  status: boolean
}

export interface DevicePolicy {
  policyId?: number
  name: string
  description?: string
  actions: string
  active: boolean
  threatLevel: string
  gracePeriod?: string | null
  actionAssigned: boolean
  alertSeverity: string
  deviceIntegrityPeriod: string
  outdatedOSPeriod: string
  outdatedOSPeriodOffset: number
  screenLockEnabled: string
  smishingEnabled: string
  biometricsEnabled: string
  travelEnabled: string
  createdBy: string
  createdAt?: string
  updatedAt?: string

  // OS Version
  iosVersionOffset?: string
  iosMinimumVersion?: string
  iosMinimumVersionGracePeriod?: string
  iosMinimumVersionGracePeriodUnit?: string
  androidMinimumVersion?: string
  androidLastPatchVersionGracePeriod?: string
  androidLastPatchVersionGracePeriodUnit?: string

  groupCount?: number
}

export interface DevicePolicyUpdateActionRequest {
  policyId: number
  action: string
  actionAssigned: boolean
}

export interface DevicePolicyUpdateActiveStateRequest {
  policyId: number
  active: boolean
}

export interface DevicePolicyUpdateEnableStateRequest {
  policyId: number
  enabled: boolean
}

export interface DevicePolicyUpdateThreatLevelRequest {
  policyId: number
  threatLevel: string
}

export interface DevicePolicyUpdateGracePeriodRequest {
  policyId: number
  gracePeriod: string
}

export interface DevicePolicyUpdateNameRequest {
  policyId: number
  name: string
  description?: string
}

export interface WebhookResponse {
  success: boolean
  message: string
}

export interface Scim extends ScimEnabled {
  scimUrl: string
  authHeader: string
}

export interface ScimEnabled {
  enabled: boolean
}

export interface Mdm {
  enabled: boolean
  token: string
}

export interface EmailPreferences {
  weeklySummary: boolean
  threatAlerts: boolean
  enrollmentEmailsFrequency: string
  outdatedOsEmailsFrequency: string
}

export interface NotificationPreferences {
  notificationType: string
  topic: string
  deviceIntegrityPeriod: string
  notificationFrequency: string
  showThreatDetected: boolean | null
}

export interface EnrollEmailTemplate {
  type: string
  subject: string
  body: string
}

export type EnrollEmailTemplateInputs = {
  subject: string
  body: string
}

export interface CustomDnsConfiguration {
  customDnsValue?: string
  customDnsStatus?: CustomDnsStatus
  androidVpnEnabled?: boolean
  customDnsIosRequiresVPN?: boolean
}

export enum CustomDnsStatus {
  NOT_CONFIGURED = 'NOT_CONFIGURED',
  NETWORK_DEFAULT = 'NETWORK_DEFAULT',
  CUSTOM = 'CUSTOM',
}

export type ThreatHunterUsersResponse = {
  threatHunterUsers: ThreatHunterUser[]
  error?: string
}

export interface ThreatHunterUser {
  id: number
  name: string
  email: string
  accessCode: string
  identifier: string
  status: string
  scannedBy?: string
}

export interface HasDemoDataResponse {
  orgHasDemoData: boolean
}

export type SsoMember = {
  id: string
  name: string
  email: string
  createdAt: string
  devices: string
  duplicate?: boolean
}

export const defaultDevicePolicy: DevicePolicy = {
  policyId: undefined,
  name: '',
  description: undefined,
  actions: '',
  active: false,
  actionAssigned: false,
  alertSeverity: 'NONE',
  deviceIntegrityPeriod: 'NEVER',
  outdatedOSPeriodOffset: 0,
  outdatedOSPeriod: 'NEVER',
  screenLockEnabled: 'false',
  smishingEnabled: 'false',
  biometricsEnabled: 'false',
  travelEnabled: 'false',
  gracePeriod: null,
  threatLevel: 'LOW',
  createdBy: '',
  createdAt: undefined,
  updatedAt: undefined,

  iosMinimumVersion: undefined,
  iosVersionOffset: undefined,
  iosMinimumVersionGracePeriod: undefined,
  iosMinimumVersionGracePeriodUnit: 'DAYS',

  androidMinimumVersion: undefined,
  androidLastPatchVersionGracePeriod: undefined,
  androidLastPatchVersionGracePeriodUnit: 'DAYS',
}

export type CreatePolicyInput = {
  name: string
  description?: string
}

export type PolicyConfig = {
  deviceIntegrityPeriod: string
  alertSeverity: string
  travelRiskPeriod: string
  travelRiskLevel: string
  smishingEnabled: boolean
  biometricsEnabled: boolean
  screenLockEnabled: boolean
  travelEnabled: boolean
  iosMinimumVersion: string
  iosVersionOffset: string
  iosMinimumVersionGracePeriod: string
  iosMinimumVersionGracePeriodUnit: string
  androidMinimumVersion: string
  androidLastPatchVersionGracePeriod: string
  androidLastPatchVersionGracePeriodUnit: string
}

export type PolicyActionsInput = {
  threatLevel?: string
  gracePeriodValue?: string
  gracePeriodUnit?: string
  integrationName?: string
}
